import {
  EventsQueryKey,
  EventsSearchQueryKey,
  updateEvent,
  ShiftEvent,
  EventUpdate
} from '@domain/shifthandover'
import {ApiClient} from '@hconnect/apiclient'
import {StatusWithNoneAndCancellationAndApproverAndWorkOrderStates} from '@hconnect/common/types'
import {useUpmPlantTimezone} from '@hconnect/upm'
import {useMutation, UseMutationOptions, useQueryClient} from '@tanstack/react-query'
import {AxiosError} from 'axios'

import {Status} from '../../types/shiftHandover.types'

type Variables<S> = {
  task: ShiftEvent
  status: S
  taskResolvedComment?: string
}

type Params<S extends StatusWithNoneAndCancellationAndApproverAndWorkOrderStates = Status> = {
  plantId: string
  options?: UseMutationOptions<ShiftEvent, AxiosError, Variables<S>>
}

export const useEventUpdateStatus = <
  S extends StatusWithNoneAndCancellationAndApproverAndWorkOrderStates = Status
>({
  plantId,
  options
}: Params<S>) => {
  const queryClient = useQueryClient()
  const timezone = useUpmPlantTimezone(plantId)

  return useMutation<ShiftEvent, AxiosError, Variables<S>, unknown>({
    mutationFn: async ({task, status, taskResolvedComment}) => {
      const updatedEvent = {...task, status, taskResolvedComment} as unknown as EventUpdate
      return updateEvent(ApiClient.axiosInstance, plantId, updatedEvent, timezone)
    },
    ...options,
    onSuccess: (...args) => {
      // promise results are not required, hence we can just trigger the promises
      // This is a quick-fix for HCP-23572
      void options?.onSuccess?.(...args)
      void queryClient.invalidateQueries({queryKey: [EventsSearchQueryKey]})
      void queryClient.invalidateQueries({queryKey: [EventsQueryKey]})
    }
  })
}
